import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  RefreshCw, 
  Edit, 
  Trash2, 
  Play, 
  Pause, 
  Bot,
  Settings,
  CheckCircle,
  X,
  Clock,
  AlertCircle,
  TrendingUp,
  Save,
  MessageSquare
} from 'lucide-react';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import '../styles/scrollbar.css';
import { getApiBaseUrl } from '../config/env';

interface AIAssistant {
  id: number;
  name: string;
  type: string;
  description: string;
  config: Record<string, any>;
  trigger_events: string[];
  trigger_conditions: Record<string, any>;
  is_active: boolean;
  priority: number;
  total_tasks: number;
  successful_tasks: number;
  failed_tasks: number;
  last_run_at: string | null;
  created_at: string;
  updated_at: string;
}

interface AITask {
  id: number;
  assistant_id: number;
  task_type: string;
  status: string;
  input_data: Record<string, any>;
  output_data: Record<string, any>;
  email_id: number | null;
  thread_id: number | null;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  created_at: string;
}

interface AIStats {
  assistants: {
    total: number;
    active: number;
    inactive: number;
  };
  tasks: {
    total: number;
    completed: number;
    failed: number;
    pending: number;
    success_rate: number;
  };
  analyses: {
    total: number;
  };
  reports: {
    total: number;
  };
  manager: {
    manager_stats: {
      total_tasks_processed: number;
      successful_tasks: number;
      failed_tasks: number;
      assistants_loaded: number;
      last_process_time: string | null;
    };
    llm_stats: {
      enabled: boolean;
      stats: {
        total_requests: number;
        successful_requests: number;
        failed_requests: number;
        total_tokens_used: number;
        last_request_time: string | null;
      };
      timestamp: string;
    };
    assistants: Array<{
      id: number;
      name: string;
      type: string;
    }>;
    timestamp: string;
  };
}

interface AssistantFormData {
  name: string;
  description: string;
  system_prompt: string;
  prompt_template: string;
  trigger_events: string[];
  trigger_conditions: Record<string, any>;
  priority: number;
  config: Record<string, any>;
}

const AIAssistantsPage: React.FC = () => {
  const [assistants, setAssistants] = useState<AIAssistant[]>([]);
  const [tasks, setTasks] = useState<AITask[]>([]);
  const [stats, setStats] = useState<AIStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTab, setSelectedTab] = useState<'assistants' | 'tasks' | 'stats'>('assistants');
  const [showModal, setShowModal] = useState(false);
  const [editingAssistant, setEditingAssistant] = useState<AIAssistant | null>(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState<AssistantFormData>({
    name: '',
    description: '',
    system_prompt: '',
    prompt_template: '',
    trigger_events: [],
    trigger_conditions: {},
    priority: 1,
    config: {}
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAssistants(),
        fetchTasks(),
        fetchStats()
      ]);
    } catch (error) {
      console.error('获取AI数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssistants = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/ai/assistants`);
      if (response.ok) {
        const data = await response.json();
        setAssistants(data);
      }
    } catch (error) {
      console.error('获取AI助手列表失败:', error);
    }
  };

  const fetchTasks = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/ai/tasks?limit=20`);
      if (response.ok) {
        const data = await response.json();
        setTasks(data);
      }
    } catch (error) {
      console.error('获取AI任务列表失败:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/ai/stats`);
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('获取AI统计信息失败:', error);
    }
  };

  const toggleAssistant = async (id: number, isActive: boolean) => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/ai/assistants/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ is_active: !isActive }),
      });

      if (response.ok) {
        await fetchAssistants();
      }
    } catch (error) {
      console.error('切换AI助手状态失败:', error);
    }
  };

  const handleCreateAssistant = () => {
    setEditingAssistant(null);
    setFormData({
      name: '',
      description: '',
      system_prompt: '',
      prompt_template: '',
      trigger_events: [],
      trigger_conditions: {},
      priority: 1,
      config: {}
    });
    setShowModal(true);
  };

  const handleEditAssistant = (assistant: AIAssistant) => {
    setEditingAssistant(assistant);
    setFormData({
      name: assistant.name,
      description: assistant.description,
      system_prompt: assistant.config.system_prompt || '',
      prompt_template: assistant.config.prompt_template || '',
      trigger_events: assistant.trigger_events,
      trigger_conditions: assistant.trigger_conditions,
      priority: assistant.priority,
      config: assistant.config
    });
    setShowModal(true);
  };

  const handleSaveAssistant = async () => {
    setSaving(true);
    try {
      // 根据触发事件自动确定助手类型
      let assistantType = 'email_processor';
      if (formData.trigger_events.includes('daily_report_trigger')) {
        assistantType = 'daily_reporter';
      } else if (formData.trigger_events.includes('new_email')) {
        assistantType = 'email_processor';
      }

      const assistantData = {
        name: formData.name,
        type: assistantType,
        description: formData.description,
        config: {
          ...formData.config,
          system_prompt: formData.system_prompt,
          prompt_template: formData.prompt_template
        },
        trigger_events: formData.trigger_events,
        trigger_conditions: formData.trigger_conditions,
        priority: formData.priority
      };

      const url = editingAssistant 
        ? `${getApiBaseUrl()}/ai/assistants/${editingAssistant.id}`
        : `${getApiBaseUrl()}/ai/assistants`;
      
      const method = editingAssistant ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(assistantData),
      });

      if (response.ok) {
        await fetchAssistants();
        setShowModal(false);
        setEditingAssistant(null);
      } else {
        const error = await response.json();
        alert(`保存失败: ${error.detail}`);
      }
    } catch (error) {
      console.error('保存AI助手失败:', error);
      alert('保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAssistant = async (id: number) => {
    if (!window.confirm('确定要删除这个AI助手吗？')) return;

    try {
      const response = await fetch(`${getApiBaseUrl()}/ai/assistants/${id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        await fetchAssistants();
      }
    } catch (error) {
      console.error('删除AI助手失败:', error);
    }
  };

  const testEmailAnalysis = async (emailId: number) => {
    try {
      // 调用AI分析API - 使用新的触发助手API
      const response = await fetch(`${getApiBaseUrl()}/ai/trigger-assistant/1`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email_id: emailId })
      });

      if (response.ok) {
        alert('AI分析任务已创建');
        await fetchTasks();
      }
    } catch (error) {
      console.error('测试AI分析失败:', error);
    }
  };

  const generateDailyReport = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/ai/daily-reports/generate`, {
        method: 'POST',
      });

      if (response.ok) {
        alert('日报生成任务已创建');
        await fetchTasks();
      }
    } catch (error) {
      console.error('生成日报失败:', error);
    }
  };

  const getTypeText = (type: string) => {
    const typeMap: Record<string, string> = {
      'email_processor': '邮件处理助手',
      'daily_reporter': '日报生成助手',
      'sentiment_analyzer': '情感分析助手',
      'category_manager': '分类管理助手'
    };
    return typeMap[type] || type;
  };

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      'completed': 'text-green-600 bg-green-50',
      'failed': 'text-red-600 bg-red-50',
      'pending': 'text-yellow-600 bg-yellow-50',
      'running': 'text-blue-600 bg-blue-50'
    };
    return colorMap[status] || 'text-gray-600 bg-gray-50';
  };

  const getStatusText = (status: string) => {
    const textMap: Record<string, string> = {
      'completed': '已完成',
      'failed': '失败',
      'pending': '待处理',
      'running': '运行中'
    };
    return textMap[status] || status;
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '从未运行';
    let date: Date;
    if (dateString.includes('T') && !dateString.includes('Z') && !dateString.includes('+')) {
      date = new Date(dateString + 'Z');
    } else {
      date = new Date(dateString);
    }
    return date.toLocaleString('zh-CN', { timeZone: 'Asia/Shanghai' });
  };

  const filteredAssistants = assistants.filter(assistant =>
    assistant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    assistant.description.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const triggerEventOptions = [
    { 
      value: 'new_email', 
      label: '新邮件到达',
      description: '当收到新邮件时自动触发AI分析',
      availableVariables: [
        'email_content', 'subject', 'sender', 'recipient', 'sender_name', 
        'received_date', 'thread_id', 'attachments', 'email_id', 'is_important',
        'email_size', 'has_attachments', 'attachment_count'
      ]
    },
    { 
      value: 'daily_report_trigger', 
      label: '每日报告触发',
      description: '每日定时生成邮件分析报告和统计数据',
      availableVariables: [
        'report_date', 'total_emails', 'user_emails', 'categories_summary',
        'sentiment_summary', 'priority_summary', 'email_list', 'top_senders',
        'response_rate', 'avg_response_time', 'unread_count'
      ]
    },
    { 
      value: 'manual_trigger', 
      label: '手动触发',
      description: '用户手动执行AI任务，可自定义输入内容',
      availableVariables: [
        'custom_input', 'trigger_time', 'user_id', 'selected_emails',
        'date_range', 'filter_conditions'
      ]
    }
  ];

  // 获取当前选中触发事件的可用变量
  const getAvailableVariables = () => {
    const variables = new Set<string>();
    formData.trigger_events.forEach(event => {
      const eventOption = triggerEventOptions.find(option => option.value === event);
      if (eventOption) {
        eventOption.availableVariables.forEach(variable => variables.add(variable));
      }
    });
    return Array.from(variables);
  };

  // 生成提示词模板示例
  const generateTemplateExample = () => {
    const availableVars = getAvailableVariables();
    if (formData.trigger_events.includes('new_email')) {
      return `请分析以下邮件：

邮件主题：{subject}
发件人：{sender} ({sender_name})
收件时间：{received_date}
邮件大小：{email_size}
是否重要：{is_important}
附件情况：{has_attachments ? '有附件' : '无附件'} ({attachment_count}个)

邮件内容：
{email_content}

请提供以下分析：
1. 内容摘要（150字以内）
2. 邮件分类（工作/个人/营销/通知等）
3. 情感分析（积极/中性/消极）
4. 优先级评分（1-10分，10分最高）
5. 关键标签（3-5个）
6. 是否需要回复及建议回复内容
7. 处理建议（立即处理/稍后处理/归档等）`;
    } else if (formData.trigger_events.includes('daily_report_trigger')) {
      return `请生成 {report_date} 的邮件处理分析报告：

📊 统计数据：
- 总邮件数：{total_emails}
- 用户邮件数：{user_emails}
- 未读邮件数：{unread_count}
- 平均响应时间：{avg_response_time}
- 回复率：{response_rate}%

📈 分类统计：{categories_summary}
😊 情感分析：{sentiment_summary}
⭐ 优先级分布：{priority_summary}
👥 主要发件人：{top_senders}

请提供：
1. 📋 数据总结和关键指标
2. 💡 重要洞察（3-5条）
3. 🎯 效率改进建议（3条）
4. 📊 趋势分析和预测
5. ⚠️ 需要关注的问题`;
    } else if (formData.trigger_events.includes('manual_trigger')) {
      return `手动分析任务：

输入内容：{custom_input}
触发时间：{trigger_time}
用户ID：{user_id}
选中邮件：{selected_emails}
日期范围：{date_range}
筛选条件：{filter_conditions}

请根据用户的具体需求进行分析：
1. 理解用户意图
2. 分析相关数据
3. 提供有价值的洞察
4. 给出可行的建议`;
    }
    return `请根据触发数据进行分析：\n\n可用变量：${availableVars.map(v => `{${v}}`).join(', ')}`;
  };

  // 生成专业的系统提示词示例
  const generateSystemPromptExample = () => {
    if (formData.trigger_events.includes('new_email')) {
      return `你是一位资深的邮件管理专家和商务沟通顾问，拥有超过10年的企业邮件处理经验。你的专长包括：

🎯 **核心能力**：
- 快速理解邮件内容的核心意图和重要信息
- 准确识别邮件的紧急程度和优先级
- 提供专业的商务沟通建议和回复策略
- 高效的邮件分类和标签管理

📋 **分析原则**：
- 保持客观、专业的分析态度
- 注重实用性和可操作性
- 考虑商务礼仪和沟通效果
- 提供简洁明了的建议

🔍 **分析重点**：
- 识别关键信息和行动项
- 评估发件人的意图和期望
- 判断回复的必要性和紧急程度
- 提供个性化的处理建议

💡 **输出要求**：
- 使用结构化的格式输出分析结果
- 提供具体可行的行动建议
- 保持专业而友好的语调
- 确保分析的准确性和实用性

请始终以提高用户邮件处理效率和沟通质量为目标，提供有价值的洞察和建议。`;
    } else if (formData.trigger_events.includes('daily_report_trigger')) {
      return `你是一位专业的数据分析师和效率优化专家，专门从事邮件处理数据分析和工作效率提升。你的专业背景包括：

📊 **专业领域**：
- 邮件数据统计分析和可视化
- 工作效率模式识别和优化建议
- 沟通行为分析和趋势预测
- 个人和团队生产力提升策略

🎯 **分析目标**：
- 发现邮件处理中的效率瓶颈
- 识别重要的沟通模式和趋势
- 提供数据驱动的改进建议
- 帮助用户优化时间管理

📈 **报告特色**：
- 数据可视化和趋势分析
- 实用的效率提升建议
- 个性化的优化方案
- 前瞻性的预测和警示

💼 **专业态度**：
- 基于数据的客观分析
- 注重实际应用价值
- 提供可量化的改进指标
- 保持积极正面的建议导向

请以专业、客观的态度分析邮件数据，提供有价值的洞察和可执行的改进建议，帮助用户持续优化邮件处理效率。`;
    } else if (formData.trigger_events.includes('manual_trigger')) {
      return `你是一位全能的AI助手，具备强大的分析能力和丰富的知识储备。你能够：

🧠 **核心能力**：
- 理解复杂的用户需求和意图
- 进行深度的数据分析和洞察挖掘
- 提供创新的解决方案和建议
- 适应各种不同的分析场景

🎯 **服务理念**：
- 以用户需求为中心
- 提供个性化的分析服务
- 注重实用性和可操作性
- 保持专业和友好的服务态度

🔧 **分析方法**：
- 结构化思维和逻辑分析
- 多角度综合考虑问题
- 数据驱动的决策支持
- 创新思维和最佳实践结合

💡 **输出标准**：
- 清晰的分析逻辑和结论
- 具体可行的行动建议
- 易于理解的表达方式
- 有价值的洞察和启发

请根据用户的具体需求，灵活运用你的专业能力，提供高质量的分析服务和有价值的建议。`;
    }
    return `你是一位专业的AI助手，请根据具体的触发场景提供相应的专业服务。`;
  };

  // 更新统计信息显示逻辑
  const renderStatsTab = () => {
    if (!stats) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-500">暂无统计数据</div>
        </div>
      );
    }

    return (
      <div className="h-[calc(100vh-300px)] overflow-y-auto pr-2 custom-scrollbar">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* AI助手统计 */}
          <div className="bg-blue-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold mb-4 flex items-center">
              <Bot className="w-5 h-5 mr-2 text-blue-600" />
              AI助手统计
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>总数:</span>
                <span className="font-medium">{stats.assistants.total}</span>
              </div>
              <div className="flex justify-between">
                <span>活跃:</span>
                <span className="font-medium text-green-600">{stats.assistants.active}</span>
              </div>
              <div className="flex justify-between">
                <span>停用:</span>
                <span className="font-medium text-gray-600">{stats.assistants.inactive}</span>
              </div>
            </div>
          </div>

          {/* 任务统计 */}
          <div className="bg-green-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold mb-4 flex items-center">
              <Clock className="w-5 h-5 mr-2 text-green-600" />
              任务统计
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>总任务:</span>
                <span className="font-medium">{stats.tasks.total}</span>
              </div>
              <div className="flex justify-between">
                <span>已完成:</span>
                <span className="font-medium text-green-600">{stats.tasks.completed}</span>
              </div>
              <div className="flex justify-between">
                <span>失败:</span>
                <span className="font-medium text-red-600">{stats.tasks.failed}</span>
              </div>
              <div className="flex justify-between">
                <span>待处理:</span>
                <span className="font-medium text-yellow-600">{stats.tasks.pending}</span>
              </div>
              <div className="flex justify-between">
                <span>成功率:</span>
                <span className="font-medium">{stats.tasks.success_rate.toFixed(1)}%</span>
              </div>
            </div>
          </div>

          {/* AI管理器V2统计 */}
          <div className="bg-purple-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold mb-4 flex items-center">
              <Settings className="w-5 h-5 mr-2 text-purple-600" />
              AI管理器V2
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>已加载助手:</span>
                <span className="font-medium">{stats.manager.manager_stats.assistants_loaded}</span>
              </div>
              <div className="flex justify-between">
                <span>处理任务数:</span>
                <span className="font-medium">{stats.manager.manager_stats.total_tasks_processed}</span>
              </div>
              <div className="flex justify-between">
                <span>成功任务:</span>
                <span className="font-medium text-green-600">{stats.manager.manager_stats.successful_tasks}</span>
              </div>
              <div className="flex justify-between">
                <span>失败任务:</span>
                <span className="font-medium text-red-600">{stats.manager.manager_stats.failed_tasks}</span>
              </div>
              <div className="flex justify-between">
                <span>最后处理:</span>
                <span className="font-medium text-sm">
                  {stats.manager.manager_stats.last_process_time 
                    ? formatDate(stats.manager.manager_stats.last_process_time)
                    : '从未处理'
                  }
                </span>
              </div>
            </div>
          </div>

          {/* LLM客户端统计 */}
          <div className="bg-orange-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold mb-4 flex items-center">
              <MessageSquare className="w-5 h-5 mr-2 text-orange-600" />
              LLM客户端
            </h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>状态:</span>
                <Badge variant={stats.manager.llm_stats.enabled ? 'success' : 'default'}>
                  {stats.manager.llm_stats.enabled ? '已启用' : '已禁用'}
                </Badge>
              </div>
              <div className="flex justify-between">
                <span>总请求:</span>
                <span className="font-medium">{stats.manager.llm_stats.stats.total_requests}</span>
              </div>
              <div className="flex justify-between">
                <span>成功请求:</span>
                <span className="font-medium text-green-600">{stats.manager.llm_stats.stats.successful_requests}</span>
              </div>
              <div className="flex justify-between">
                <span>失败请求:</span>
                <span className="font-medium text-red-600">{stats.manager.llm_stats.stats.failed_requests}</span>
              </div>
              <div className="flex justify-between">
                <span>Token使用:</span>
                <span className="font-medium">{stats.manager.llm_stats.stats.total_tokens_used.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span>最后请求:</span>
                <span className="font-medium text-sm">
                  {stats.manager.llm_stats.stats.last_request_time 
                    ? formatDate(stats.manager.llm_stats.stats.last_request_time)
                    : '从未请求'
                  }
                </span>
              </div>
            </div>
          </div>

          {/* 分析统计 */}
          <div className="bg-blue-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold mb-4">邮件分析</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>总分析数:</span>
                <span className="font-medium">{stats.analyses.total}</span>
              </div>
            </div>
          </div>

          {/* 报告统计 */}
          <div className="bg-green-50 rounded-lg p-6">
            <h4 className="text-lg font-semibold mb-4">日报统计</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>总报告数:</span>
                <span className="font-medium">{stats.reports.total}</span>
              </div>
            </div>
          </div>

          {/* 已加载的助手列表 */}
          {stats.manager.assistants.length > 0 && (
            <div className="bg-gray-50 rounded-lg p-6 md:col-span-2">
              <h4 className="text-lg font-semibold mb-4">已加载的助手</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {stats.manager.assistants.map((assistant) => (
                  <div key={assistant.id} className="bg-white rounded-lg p-3 border border-gray-200">
                    <div className="font-medium text-gray-900">{assistant.name}</div>
                    <div className="text-sm text-gray-600">{getTypeText(assistant.type)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* 系统信息 */}
          <div className="bg-yellow-50 rounded-lg p-6 md:col-span-2">
            <h4 className="text-lg font-semibold mb-4">系统信息</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-gray-600">管理器时间戳:</span>
                <div className="font-medium">{formatDate(stats.manager.timestamp)}</div>
              </div>
              <div>
                <span className="text-gray-600">LLM时间戳:</span>
                <div className="font-medium">{formatDate(stats.manager.llm_stats.timestamp)}</div>
              </div>
              <div>
                <span className="text-gray-600">API状态:</span>
                <div className="font-medium">
                  <Badge variant="success">正常</Badge>
                </div>
              </div>
              <div>
                <span className="text-gray-600">架构版本:</span>
                <div className="font-medium">V2</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">AI助手管理</h1>
          <p className="text-gray-600 mt-1">
            管理和配置您的AI助手，自动化邮件处理流程
          </p>
        </div>
        <div className="flex space-x-3">
          <Button onClick={fetchData} loading={loading} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            刷新
          </Button>
          <Button onClick={handleCreateAssistant}>
            <Plus className="w-4 h-4 mr-2" />
            新建助手
          </Button>
        </div>
      </div>

      {/* 标签页 */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'assistants', label: 'AI助手', icon: Bot },
            { id: 'tasks', label: '任务记录', icon: Clock },
            { id: 'stats', label: '统计信息', icon: TrendingUp }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setSelectedTab(tab.id as any)}
                className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                  selectedTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* 内容区域 */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="p-6">
          {selectedTab === 'assistants' && (
            <div className="h-[calc(100vh-300px)]">
              {/* 搜索栏 */}
              <div className="mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="搜索AI助手..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* AI助手列表 - 可滚动区域 */}
              <div className="h-[calc(100%-88px)] overflow-y-auto pr-2 custom-scrollbar">
                <div className="space-y-4">
                  {filteredAssistants.length > 0 ? (
                    filteredAssistants.map((assistant) => (
                      <div key={assistant.id} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900">
                                {assistant.name}
                              </h3>
                              <Badge variant={assistant.is_active ? 'success' : 'default'}>
                                {assistant.is_active ? '运行中' : '已停止'}
                              </Badge>
                              <Badge variant="info">
                                {getTypeText(assistant.type)}
                              </Badge>
                            </div>
                            
                            <p className="text-gray-600 mb-4">{assistant.description}</p>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                              <div>
                                <span className="text-gray-500">总任务:</span>
                                <span className="ml-1 font-medium">{assistant.total_tasks}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">成功:</span>
                                <span className="ml-1 font-medium text-green-600">{assistant.successful_tasks}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">失败:</span>
                                <span className="ml-1 font-medium text-red-600">{assistant.failed_tasks}</span>
                              </div>
                              <div>
                                <span className="text-gray-500">最后运行:</span>
                                <span className="ml-1 font-medium">{formatDate(assistant.last_run_at)}</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center space-x-2 ml-4">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => toggleAssistant(assistant.id, assistant.is_active)}
                            >
                              {assistant.is_active ? (
                                <>
                                  <Pause className="w-4 h-4 mr-1" />
                                  停止
                                </>
                              ) : (
                                <>
                                  <Play className="w-4 h-4 mr-1" />
                                  启动
                                </>
                              )}
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleEditAssistant(assistant)}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              编辑
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleEditAssistant(assistant)}
                            >
                              <Settings className="w-4 h-4 mr-1" />
                              配置
                            </Button>
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleDeleteAssistant(assistant.id)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-12">
                      <Bot className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2">暂无AI助手</h3>
                      <p className="text-gray-500 mb-4">
                        {searchTerm ? '没有找到匹配的AI助手' : '还没有创建任何AI助手'}
                      </p>
                      <Button onClick={handleCreateAssistant}>
                        <Plus className="w-4 h-4 mr-2" />
                        创建第一个AI助手
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {selectedTab === 'tasks' && (
            <div className="h-[calc(100vh-300px)]">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">最近任务</h3>
                <div className="flex space-x-2">
                  <Button variant="outline" size="sm" onClick={() => testEmailAnalysis(1)}>
                    测试邮件分析
                  </Button>
                  <Button variant="outline" size="sm" onClick={generateDailyReport}>
                    生成日报
                  </Button>
                </div>
              </div>

              {/* 任务列表 - 可滚动区域 */}
              <div className="h-[calc(100%-88px)] overflow-y-auto custom-scrollbar">
                {tasks.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            任务类型
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            状态
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            创建时间
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            完成时间
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            错误信息
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {tasks.map((task) => (
                          <tr key={task.id}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {task.task_type}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Badge className={getStatusColor(task.status)}>
                                {getStatusText(task.status)}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(task.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {formatDate(task.completed_at)}
                            </td>
                            <td className="px-6 py-4 text-sm text-red-600 max-w-xs truncate">
                              {task.error_message || '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">暂无任务记录</h3>
                    <p className="text-gray-500 mb-4">
                      还没有执行过任何AI任务
                    </p>
                    <div className="flex justify-center space-x-2">
                      <Button variant="outline" size="sm" onClick={() => testEmailAnalysis(1)}>
                        测试邮件分析
                      </Button>
                      <Button variant="outline" size="sm" onClick={generateDailyReport}>
                        生成日报
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {selectedTab === 'stats' && renderStatsTab()}
        </div>
      </div>

      {/* AI助手编辑/创建模态框 */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold">
                {editingAssistant ? '编辑AI助手' : '新建AI助手'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* 基本信息 */}
              <div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    助手名称
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-md"
                    placeholder="输入助手名称"
                  />
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    助手描述
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full p-2 border border-gray-300 rounded-md h-20"
                    placeholder="描述这个AI助手的功能和用途"
                  />
                </div>
              </div>

              {/* 第一步：选择触发时机 */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  第一步：选择触发时机
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  选择什么时候触发AI助手，不同的触发时机会提供不同的数据变量
                </p>
                
                <div className="space-y-3">
                  {triggerEventOptions.map((option) => (
                    <div key={option.value} className="border border-gray-200 rounded-lg p-4">
                      <label className="flex items-start cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.trigger_events.includes(option.value)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData({
                                ...formData,
                                trigger_events: [...formData.trigger_events, option.value]
                              });
                            } else {
                              setFormData({
                                ...formData,
                                trigger_events: formData.trigger_events.filter(event => event !== option.value)
                              });
                            }
                          }}
                          className="mt-1 mr-3"
                        />
                        <div className="flex-1">
                          <div className="font-medium text-gray-900">{option.label}</div>
                          <div className="text-sm text-gray-600 mt-1">{option.description}</div>
                          <div className="text-xs text-gray-500 mt-2">
                            可用变量: {option.availableVariables.map(v => `{${v}}`).join(', ')}
                          </div>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              {/* 第二步：可用变量和模板示例 */}
              {formData.trigger_events.length > 0 && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    第二步：查看可用数据变量
                  </h3>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <h4 className="font-medium text-blue-900 mb-2">当前可用变量：</h4>
                    <div className="flex flex-wrap gap-2">
                      {getAvailableVariables().map((variable) => (
                        <span
                          key={variable}
                          className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-800 cursor-pointer"
                          onClick={() => {
                            const textarea = document.querySelector('textarea[placeholder*="提示词模板"]') as HTMLTextAreaElement;
                            if (textarea) {
                              const cursorPos = textarea.selectionStart;
                              const textBefore = formData.prompt_template.substring(0, cursorPos);
                              const textAfter = formData.prompt_template.substring(textarea.selectionEnd);
                              const newText = textBefore + `{${variable}}` + textAfter;
                              setFormData({ ...formData, prompt_template: newText });
                              setTimeout(() => {
                                textarea.focus();
                                textarea.setSelectionRange(cursorPos + variable.length + 2, cursorPos + variable.length + 2);
                              }, 0);
                            }
                          }}
                        >
                          {`{${variable}}`}
                        </span>
                      ))}
                    </div>
                    <p className="text-xs text-blue-700 mt-2">
                      点击变量可以插入到提示词模板中
                    </p>
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">推荐模板示例：</h4>
                    <pre className="text-xs text-gray-700 whitespace-pre-wrap font-mono">
                      {generateTemplateExample()}
                    </pre>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, prompt_template: generateTemplateExample() })}
                      className="mt-2 text-xs text-blue-600 hover:text-blue-800"
                    >
                      使用此模板
                    </button>
                  </div>
                </div>
              )}

              {/* 第三步：编写提示词 */}
              {formData.trigger_events.length > 0 && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    第三步：编写AI提示词
                  </h3>

                  {/* 系统提示词 */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        <MessageSquare className="w-4 h-4 inline mr-1" />
                        系统提示词 (System Prompt)
                      </label>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, system_prompt: generateSystemPromptExample() })}
                        className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 border border-blue-300 rounded hover:bg-blue-50"
                      >
                        使用专业模板
                      </button>
                    </div>
                    <textarea
                      value={formData.system_prompt}
                      onChange={(e) => setFormData({ ...formData, system_prompt: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-md h-40 font-mono text-sm"
                      placeholder="定义AI助手的角色、行为和响应风格..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      系统提示词定义了AI助手的基本行为和角色设定，建议使用专业模板
                    </p>
                  </div>

                  {/* 格式化提示词模板 */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <MessageSquare className="w-4 h-4 inline mr-1" />
                      提示词模板 (Prompt Template)
                    </label>
                    <textarea
                      value={formData.prompt_template}
                      onChange={(e) => setFormData({ ...formData, prompt_template: e.target.value })}
                      className="w-full p-3 border border-gray-300 rounded-md h-32 font-mono text-sm"
                      placeholder="使用上面的变量编写提示词模板..."
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      使用 {'{变量名}'} 格式插入动态内容，点击上方的变量标签可以快速插入
                    </p>
                  </div>
                </div>
              )}

              {/* 第四步：高级配置 */}
              {formData.trigger_events.length > 0 && (
                <div className="border-t pt-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">
                    第四步：高级配置
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        优先级
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={formData.priority}
                        onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
                        className="w-full p-2 border border-gray-300 rounded-md"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        1-10，数字越小优先级越高
                      </p>
                    </div>
                  </div>

                  {/* 触发条件配置 */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      触发条件配置
                    </label>
                    <div className="space-y-2">
                      {formData.trigger_events.includes('new_email') && (
                        <>
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.trigger_conditions.is_user_email || false}
                              onChange={(e) => setFormData({
                                ...formData,
                                trigger_conditions: {
                                  ...formData.trigger_conditions,
                                  is_user_email: e.target.checked
                                }
                              })}
                              className="mr-2"
                            />
                            <span className="text-sm">仅处理用户邮件</span>
                          </label>
                          
                          <div className="flex items-center space-x-2">
                            <span className="text-sm">最小内容长度:</span>
                            <input
                              type="number"
                              min="0"
                              value={formData.trigger_conditions.min_content_length || 10}
                              onChange={(e) => setFormData({
                                ...formData,
                                trigger_conditions: {
                                  ...formData.trigger_conditions,
                                  min_content_length: parseInt(e.target.value)
                                }
                              })}
                              className="w-20 p-1 border border-gray-300 rounded"
                            />
                            <span className="text-sm text-gray-500">字符</span>
                          </div>

                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={formData.trigger_conditions.only_important || false}
                              onChange={(e) => setFormData({
                                ...formData,
                                trigger_conditions: {
                                  ...formData.trigger_conditions,
                                  only_important: e.target.checked
                                }
                              })}
                              className="mr-2"
                            />
                            <span className="text-sm">仅处理重要邮件</span>
                          </label>
                        </>
                      )}
                      
                      {formData.trigger_events.includes('daily_report_trigger') && (
                        <div className="flex items-center space-x-2">
                          <span className="text-sm">最少邮件数量:</span>
                          <input
                            type="number"
                            min="1"
                            value={formData.trigger_conditions.min_emails_threshold || 1}
                            onChange={(e) => setFormData({
                              ...formData,
                              trigger_conditions: {
                                ...formData.trigger_conditions,
                                min_emails_threshold: parseInt(e.target.value)
                              }
                            })}
                            className="w-20 p-1 border border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-500">封邮件才生成报告</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 操作按钮 */}
            <div className="flex justify-end space-x-3 mt-8 pt-6 border-t">
              <Button
                variant="outline"
                onClick={() => setShowModal(false)}
              >
                取消
              </Button>
              <Button
                onClick={handleSaveAssistant}
                loading={saving}
              >
                <Save className="w-4 h-4 mr-2" />
                {editingAssistant ? '更新' : '创建'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIAssistantsPage; 